import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import forgeLogoSrc from "@/assets/forge-mark.svg";

/** Milliseconds before the loader shows a "taking too long" recovery UI. */
const LOADER_STALL_THRESHOLD_MS = 12_000;

/**
 * Full-page loader matching the pre-React splash screen.
 *
 * Shows the Forge logo centered on a dark-blue background with an animated
 * progress bar underneath. Used for route transitions, Suspense fallbacks,
 * and auth/permission loading gates.
 *
 * After {@link LOADER_STALL_THRESHOLD_MS} the loader displays a recovery
 * message with options to retry or navigate home, preventing the user from
 * being stuck on an indefinite spinner.
 */
export function ForgePageLoader({ className }: { className?: string }) {
  const [isStalled, setIsStalled] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsStalled(true), LOADER_STALL_THRESHOLD_MS);
    return () => clearTimeout(timer);
  }, []);

  const handleRetry = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = "/today";
  };

  return (
    <div
      data-route-loading=""
      className={cn(
        "fixed inset-0 z-50 flex flex-col items-center justify-center gap-4",
        className,
      )}
      style={{ background: "#0b1120" }}
    >
      <img
        src={forgeLogoSrc}
        alt="Loading"
        width={48}
        height={48}
        className="h-12 w-12 rounded-xl object-contain"
      />

      {isStalled ? (
        <div className="flex flex-col items-center gap-3 animate-in fade-in duration-300">
          <p className="text-sm text-white/60">
            This is taking longer than expected.
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleRetry}
              className="rounded-md bg-white/10 px-4 py-2 text-sm font-medium text-white/80 transition-colors hover:bg-white/20"
            >
              Retry
            </button>
            <button
              onClick={handleGoHome}
              className="rounded-md bg-white/5 px-4 py-2 text-sm text-white/50 transition-colors hover:bg-white/10 hover:text-white/70"
            >
              Go to Home
            </button>
          </div>
        </div>
      ) : (
        <div className="relative h-[3px] w-[120px] overflow-hidden rounded-sm bg-white/[0.08]">
          <div className="absolute inset-0 animate-splash-slide bg-white/30" />
        </div>
      )}
    </div>
  );
}

/**
 * Inline loader for buttons and small spaces.
 * Drops in as a replacement for Loader2 from lucide-react.
 */
export function ForgeSpinner({
  className,
  size = "sm",
}: {
  className?: string;
  size?: "xs" | "sm" | "md";
}) {
  const sizeClasses = {
    xs: { classes: "w-4 h-4", px: 16 },
    sm: { classes: "w-5 h-5", px: 20 },
    md: { classes: "w-6 h-6", px: 24 },
  };

  const { classes, px } = sizeClasses[size];

  return (
    <img
      src={forgeLogoSrc}
      alt="Loading"
      width={px}
      height={px}
      className={cn(
        classes,
        "object-contain animate-forge-pulse",
        className,
      )}
    />
  );
}
